import { getNthIndex } from 'lib/utils/helpers';
import { ProductCard } from 'components/shared';
import GridBanner from './gridBanner';

const repeat = (rows, columns) => new Array(rows).fill(columns);

const templates = {
  default: [1, 1, 2, 2, ...repeat(7, 1)],
  1: [1],
  2: [2],
};

const desktopTemplates = {
  default: [2, 4, ...repeat(5, 3)],
  2: [2],
  3: [3],
  4: [4],
};

/**
 *  @return {Array} accumulated index split per
 *  [
 *    [0,1,2]
 *    [3,4,5,6,7]
 *  ]
 */
const getCellsPerRow = template =>
  template.reduce(
    (rows, columns) => [
      ...rows,
      Array(columns)
        .fill()
        .map((_, i) => i + rows.flat().length),
    ],
    []
  );

const getRowCells = (cellsPerRow, index) => {
  const templateIndex = getNthIndex(index, cellsPerRow.flat().length);
  return cellsPerRow.find(cells => cells.includes(templateIndex));
};

const getBannerWidth = (enabled, rowCells, index, idealWidth) => {
  if (!enabled) return 0;

  const rowCellIndex = rowCells.indexOf(index);
  const availableCells = rowCells.length - rowCellIndex;

  if (availableCells < idealWidth) return availableCells;

  return idealWidth;
};

const getVisibilityClassName = (showMobile, showDesktop, visibleClassName) => {
  if (showMobile && showDesktop) return '';
  if (!showMobile && !showDesktop) return '';
  if (showMobile) return `${visibleClassName} md:hidden`;
  return `hidden md:${visibleClassName}`;
};

const columns = 12;

const ProductGrid = ({ products, banners, layout, desktopLayout, t, toggleQuickShop }) => {
  const template = templates[layout] || templates.default;
  const desktopTemplate = desktopTemplates[desktopLayout] || desktopTemplates.default;

  const cellsPerRow = getCellsPerRow(template);
  const desktopCellsPerRow = getCellsPerRow(desktopTemplate);

  const items = products.reduce((accumulator, product, i) => {
    const mobileBanner = banners.find(({ index }) => index === i);
    const desktopBanner = banners.find(({ indexDesktop }) => indexDesktop === i);

    const isMobile = !!mobileBanner;
    const isDesktop = !!desktopBanner;
    const addBanner = isMobile || isDesktop;

    const banner = {
      ...(mobileBanner || {}),
      ...(desktopBanner || {}),
      isMobile,
      isDesktop,
    };

    return [...accumulator, ...(addBanner ? [banner] : []), product];
  }, []);

  // Skip template cell if one element takes up more than one cell
  let shift = 0;
  let desktopShift = 0;

  const renderItem = (item, i) => {
    const { __typename: itemType } = item || {};

    const mobileIndex = i + shift;
    const desktopIndex = i + desktopShift;

    const rowCells = getRowCells(cellsPerRow, mobileIndex);
    const span = columns / rowCells.length;

    const desktopRowCells = getRowCells(desktopCellsPerRow, desktopIndex);
    const desktopSpan = columns / desktopRowCells.length;

    const colSpanClassName = `col-[span_var(--colSpan)_/_span_var(--colSpan)] 
    md:col-[span_var(--desktopColSpan)_/_span_var(--desktopColSpan)]`;

    if (itemType === 'ComponentGridBanner') {
      const { isDesktop, isMobile } = item;

      const bannerWidth = getBannerWidth(isMobile, rowCells, mobileIndex, 2);
      const desktopIdealBannerWidth = desktopRowCells.length > 2 ? 2 : 1;
      const desktopBannerWidth = getBannerWidth(
        isDesktop,
        desktopRowCells,
        desktopIndex,
        desktopIdealBannerWidth
      );

      shift += bannerWidth - 1;
      desktopShift += desktopBannerWidth - 1;

      const visibilityClassName = getVisibilityClassName(isMobile, isDesktop, 'flex');

      return (
        <GridBanner
          key={itemType + mobileIndex}
          style={{
            '--colSpan': bannerWidth * span,
            '--desktopColSpan': desktopBannerWidth * desktopSpan,
          }}
          className={`${colSpanClassName} ${visibilityClassName} pt-[136.3275%] md:mb-[66px] md:pt-0`}
          banner={item}
        />
      );
    }

    return (
      <ProductCard
        style={{ '--colSpan': span, '--desktopColSpan': desktopSpan }}
        className={colSpanClassName}
        key={item.id}
        product={item}
        t={t}
        priority={i < 4}
        viewWidths={[100 / rowCells.length - 15, 100 / desktopRowCells.length - 5]}
        toggleQuickShop={toggleQuickShop}
      />
    );
  };

  return <section className="grid grid-cols-12 gap-4 px-4 pb-4">{items.map(renderItem)}</section>;
};

export default ProductGrid;
